"use client";

import Logo from "@/shared/LogoFooter";
import SocialsList1 from "@/shared/SocialsList1";
import { CustomLink } from "@/data/types";
import React, { useEffect, useState } from "react";
import axios from "axios";
import FooterNav from "./FooterNav";
import AppInstallBanner from "./AppInstallBanner";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";

const API_URL = process.env.NEXT_PUBLIC_SOCKET ? `${process.env.NEXT_PUBLIC_SOCKET}/api` : 'http://localhost:3001/api';

const formatWhatsappNumber = (raw?: string) => {
  if (!raw) return '';
  const match = raw.match(/^\+(\d{1,3})(\d{3})(\d{3})(\d{4})$/);
  if (match) return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
  return raw;
};

const buildWhatsappLink = (raw?: string) => {
  if (!raw) return '#';
  const digits = raw.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
};

export interface WidgetFooterMenu {
  id: string;
  title: string;
  menus: CustomLink[];
}

const Footer: React.FC = () => {
  const { t } = useTranslation("guydeez");
  const connectedUser = useAppSelector((state) => state.auth.user);
  const sns = useAppSelector((state) => state.settings.socialNetworks);
  const selectedLanguage = useAppSelector((state) => state.language.selectedLanguage);
  const version = "v-1.260323";
  const [whatsappNumbers, setWhatsappNumbers] = useState<{ GUIDES?: string; CUSTOMER?: string }>({});

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/whatsapp/public-numbers`)
      .then((response) => {
        if (cancelled) return;
        if (response.data?.success) {
          setWhatsappNumbers(response.data.numbers || {});
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const widgetMenus: WidgetFooterMenu[] = [
    {
      id: "5",
      title: t("footer_our_tours"),
      menus: [
        { href: "tours?cat=private-tour", label: t("footer_private_tour") },
        { href: "tours?cat=group-tour", label: t("footer_group_tour") },
        { href: "tours?cat=street-art-tour", label: t("footer_street_art_tour") },
        { href: "tours?cat=bike-tour", label: t("footer_bike_tour") },
        { href: "tours?cat=food-tour", label: t("footer_food_tour") },
        { href: "tours?cat=excursion", label: t("footer_excursion") },
        { href: "tours?cat=private-car-tour", label: t("footer_private_car_tour") },
        { href: "tours?cat=museum", label: t("footer_museum_tour") },
      ],
    },
    {
      id: "1",
      title: t("footer_search_by_zone"),
      menus: [
        { href: "/continent/africa", label: t("footer_africa") },
        { href: "/continent/america", label: t("footer_america") },
        { href: "/continent/asia", label: t("footer_asia") },
        { href: "/continent/europa", label: t("footer_europa") },
        { href: "/continent/oceania", label: t("footer_oceania") },
      ],
    },
    {
      id: "2",
      title: t("footer_more"),
      menus: [
        //{ href: "/gift-cards", label: t("footer_gift_cards") },
        //{ href: "/blog", label: t("footer_our_blog") },
        { href: "/faq", label: t("footer_support_faq") },
        { href: "/about", label: t("footer_about_us") },
        { href: "/contact", label: t("footer_contact_us") },
        // CGU = Conditions Générales d'Utilisation. Same CMS-backed
        // page that the checkout disclaimer links to.
        {
          href: "/terms-and-conditions",
          label: t("footer_terms_and_conditions", {
            defaultValue: "Conditions générales",
          }),
        },
        ...(connectedUser ? [] : [{ href: "/login", label: t("footer_login_signup") }]),
      ],
    },
    {
      id: "4",
      title: "Guydeez Tour FZCO",
      menus: [
        { href: "#", label: "info@guydeez.com" },
        ...(whatsappNumbers.GUIDES
          ? [{
              href: buildWhatsappLink(whatsappNumbers.GUIDES),
              label: `${t('contact_whatsapp_guides')}\n${formatWhatsappNumber(whatsappNumbers.GUIDES)}`,
              targetBlank: true,
            }]
          : []),
        ...(whatsappNumbers.CUSTOMER
          ? [{
              href: buildWhatsappLink(whatsappNumbers.CUSTOMER),
              label: `${t('contact_whatsapp_customer')}\n${formatWhatsappNumber(whatsappNumbers.CUSTOMER)}`,
              targetBlank: true,
            }]
          : []),
        { href: "#", label: "Guydeez" },
        { href: "#", label: "IFZA Business Park, DDP," },
        { href: "#", label: "PO Box 342001, Dubai, United Arab Emirates" },
      ],
    },
  ];

  return (
    <>
      {/*<FooterNav />*/}

      <div className="bg-orange-50 lg:bg-transparent nc-Footer relative py-4 pb-24 lg:py-28 border-t border-neutral-200 dark:border-neutral-700">
        {/* Rôles 1 et 2 uniquement : le composant ne rend rien pour les autres. */}
        <AppInstallBanner />

        <div className="container grid grid-cols-2 gap-y-10 gap-x-5 sm:gap-x-8 md:grid-cols-4 lg:grid-cols-5 lg:gap-x-10">
          {/* Logo column — explicit flex-col so the social row sits cleanly
              under the logo image with a real gap (the previous mix of
              `lg:grid` + `lg:flex` could leave the social row visually
              tucked inside the logo's transparent padding). */}
          <div className="hidden lg:flex lg:flex-col col-span-2 md:col-span-4 lg:col-span-1 items-start">
            <Logo />
            {sns.some((s) => {
              const linkKey = `link${selectedLanguage?.toUpperCase().slice(-2)}`;
              return !!(s[linkKey as keyof typeof s] as string);
            }) && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">
                  {t("footer_follow_us", { defaultValue: "Suivez-nous sur" })}
                </p>
                <div className="flex items-center gap-3">
                  {sns.map((s) => {
                    const linkKey = `link${selectedLanguage?.toUpperCase().slice(-2)}`;
                    const link = s[linkKey as keyof typeof s] as string;
                    const iconPath = `/images/icons/${s.uuid}.svg`;
                    return link ? (
                      <a
                        key={s.uuid}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.name}
                      >
                        <img src={iconPath} alt={s.name} className="h-6 w-6" />
                      </a>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>

          {widgetMenus.map((menu, index) => (
            <div key={index} className="text-sm">
              <h2 className="font-semibold text-neutral-700 dark:text-neutral-200">{menu.title}</h2>
              <ul className="mt-5 space-y-4">
                {menu.menus.map((item, itemIndex) => {
                  const newlineIndex = item.label.indexOf('\n');
                  const hasTwoLines = newlineIndex !== -1;
                  return (
                    <li key={itemIndex}>
                      <a
                        className="text-neutral-600 dark:text-neutral-300 hover:text-black dark:hover:text-white"
                        href={item.href}
                        target={item.targetBlank ? '_blank' : undefined}
                        rel={item.targetBlank ? 'noopener noreferrer' : undefined}
                      >
                        {hasTwoLines ? (
                          <>
                            <span className="block font-semibold text-neutral-700 dark:text-neutral-200">
                              {item.label.slice(0, newlineIndex)}
                            </span>
                            <span className="block">{item.label.slice(newlineIndex + 1)}</span>
                          </>
                        ) : (
                          item.label
                        )}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Horizontal bar with copyright information */}
          <div
            className="col-span-2 md:col-span-4 lg:md:col-span-1"
            style={{
              width: "100%",
              position: "absolute",
              bottom: 0,
              left: 0,
              padding: "8px 0px",
            }}
          >
            <hr className="border-t border-gray-300 my-2" />

            <p className="text-center">
              ©<span className="hidden sm:inline">2019 - {new Date().getFullYear()}</span>{' '}
              <b title={version}>Guydeez Tours</b> - Tous droits réservés
              <span className="block sm:inline">
                <span className="hidden sm:inline"> – </span>
                <span className="inline sm:hidden"> </span>
                Developed by{" "}
                <a
                  href="https://numenor.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 underline-offset-2 hover:underline"
                >
                  Numenor
                </a>
                .
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Footer;
